import React, { useEffect, useState, useCallback } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import NotificationBell from "../components/NotificationBell";
import QAConsole from "../components/QAConsole";
import LivePresenceCard from "../components/LivePresenceCard";
import BulkGrantDialog from "../components/BulkGrantDialog";
import { toast } from "sonner";
import {
  Users, DollarSign, Ticket, TrendingUp, Search, Download,
  Plus, Edit, Trash2, Star, LogOut, ArrowLeft, Radio, Video,
  CheckCircle2, XCircle, Receipt, Save, RefreshCw, Heart, Globe2,
  ShieldCheck, ShieldOff, History, Eye, EyeOff, Upload,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-3xl bg-white ring-1 ring-black/5 p-6" data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">{label}</div>
        <Icon className="text-slate-400" size={18} strokeWidth={1.5} />
      </div>
      <div className="mt-3 font-heading text-3xl tracking-tighter font-medium text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function SparkBar({ daily }) {
  const max = Math.max(1, ...daily.map((d) => d.revenue_cents));
  return (
    <div className="rounded-3xl bg-white ring-1 ring-black/5 p-6">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Revenue · 7 days</div>
        <TrendingUp className="text-slate-400" size={18} strokeWidth={1.5} />
      </div>
      <div className="mt-5 flex items-end gap-2 h-24">
        {daily.map((d) => (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5">
            <div
              className="w-full rounded-md bg-[#28BBE3]/90 hover:bg-[#28BBE3] transition-colors"
              style={{ height: `${Math.max(4, (d.revenue_cents / max) * 100)}%` }}
              title={`${d.date}: £${(d.revenue_cents / 100).toFixed(2)}`}
            />
            <span className="text-[10px] text-slate-400 font-mono">{d.date.slice(5)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
  }, []);
  if (!stats) return <div className="p-6 text-slate-500">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <LivePresenceCard />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total signups" value={stats.total_users || 0} />
        <StatCard icon={Ticket} label="Ticket holders" value={stats.ticket_holders || 0} sub={`${stats.tickets_sold || 0} paid sales`} />
        <StatCard icon={DollarSign} label="Revenue" value={`£${(stats.revenue_gbp || 0).toFixed(2)}`} sub="GBP · lifetime" />
        <StatCard icon={Receipt} label="Avg. ticket" value={`£${stats.tickets_sold ? (stats.revenue_gbp / stats.tickets_sold).toFixed(2) : "0.00"}`} />
      </div>

      {/* Tier breakdown */}
      <div className="rounded-3xl bg-white ring-1 ring-black/5 p-6 sm:p-7" data-testid="admin-tier-breakdown">
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Tickets by tier</div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-2xl bg-slate-50 px-5 py-4" data-testid="tier-full">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Full</div>
            <div className="mt-1 font-heading text-3xl font-medium text-slate-900 tracking-tight">{stats.tickets_full ?? 0}</div>
            <div className="mt-1 text-xs text-slate-500">£397 / £447 paid</div>
          </div>
          <div className="rounded-2xl bg-slate-50 px-5 py-4" data-testid="tier-concession">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Concession</div>
            <div className="mt-1 font-heading text-3xl font-medium text-slate-900 tracking-tight">{stats.tickets_concession ?? 0}</div>
            <div className="mt-1 text-xs text-slate-500">£247 / £277 paid</div>
          </div>
          <div className="rounded-2xl bg-slate-50 px-5 py-4" data-testid="tier-lmic">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">LMIC (free)</div>
            <div className="mt-1 font-heading text-3xl font-medium text-slate-900 tracking-tight">{stats.tickets_lmic ?? 0}</div>
            <div className="mt-1 text-xs text-slate-500">Approved applications</div>
          </div>
        </div>
      </div>
      <SparkBar daily={stats.daily || []} />
      <ConvertKitPanel />
      <CertificateOverridePanel />
      <PhotoGalleryAdminPanel />
    </div>
  );
}

function PhotoGalleryAdminPanel() {
  const [url, setUrl] = useState("");
  const [saved, setSaved] = useState(""); // last-saved value from server
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/photo-gallery")
      .then((r) => { setUrl(r.data.url || ""); setSaved(r.data.url || ""); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/photo-gallery", { url: url.trim() || null });
      setSaved(r.data.url || "");
      setUrl(r.data.url || "");
      toast.success(r.data.url ? "Photo gallery link saved" : "Photo gallery link cleared");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return null;
  const dirty = (url || "").trim() !== (saved || "").trim();

  return (
    <div className="rounded-3xl bg-white ring-1 ring-black/5 p-6 sm:p-7" data-testid="photo-gallery-admin-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Conference photos</div>
          <div className="mt-1 font-heading text-lg font-medium text-slate-900">
            {saved ? "Gallery link is live" : "No gallery link set"}
          </div>
          <div className="text-xs text-slate-500 mt-1.5 max-w-2xl leading-relaxed">
            Paste a public Google Photos / Pixieset / SmugMug album URL. In-person attendees (and admins) will see a
            <span className="font-semibold"> Conference photos</span> panel on their Portal with a
            <span className="font-semibold"> Open gallery</span> button. Virtual-only ticket holders won&apos;t see anything.
          </div>
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://photos.app.goo.gl/… — leave empty to hide the panel"
            className="mt-3 rounded-xl border-slate-200 py-4 text-xs font-mono"
            data-testid="photo-gallery-url-input"
          />
        </div>
        <Button
          onClick={save}
          disabled={saving || !dirty}
          className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white text-xs shrink-0"
          data-testid="photo-gallery-save-btn"
        >
          <Save size={12} className="mr-1" /> Save
        </Button>
      </div>
    </div>
  );
}

function CertificateOverridePanel() {
  const [enabled, setEnabled] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/certificate/override").then((r) => setEnabled(r.data.enabled)).catch(() => setEnabled(false));
  }, []);
  if (enabled === null) return null;
  const toggle = async () => {
    setSaving(true);
    try {
      const r = await api.post("/admin/certificate/override", { enabled: !enabled });
      setEnabled(r.data.enabled);
      toast.success(r.data.enabled ? "Certificate unlocked for everyone" : "Reverted to scheduled unlock (24 Jun 15:00 BST)");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-3xl bg-white ring-1 ring-black/5 p-6 sm:p-7" data-testid="cert-override-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Attendance certificate</div>
          <div className="mt-1 font-heading text-lg font-medium text-slate-900">
            {enabled ? "Manual override ON — viewers can claim now" : "Auto-unlocks 24 Jun, 15:00 BST"}
          </div>
          <div className="text-xs text-slate-500 mt-1.5 max-w-xl">
            Toggle this on to make the Download / Email Certificate buttons appear for every ticket-holder right now. Useful for testing, or to release certificates early if the closing keynote finishes ahead of schedule.
          </div>
        </div>
        <Button
          onClick={toggle}
          disabled={saving}
          variant={enabled ? "default" : "outline"}
          className={"rounded-full text-xs " + (enabled ? "bg-emerald-600 hover:bg-emerald-700" : "")}
          data-testid="cert-override-toggle"
        >
          {enabled ? "Switch off override" : "Unlock certificate now"}
        </Button>
      </div>
    </div>
  );
}

function ConvertKitPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState({ email: "", tier: "full", name: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/convertkit/status");
      setStatus(r.data);
    } catch (e) {
      setStatus({ ok: false, error: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendTest = async () => {
    if (!form.email) { toast.error("Enter a test email"); return; }
    setTesting(true);
    try {
      await api.post("/admin/convertkit/test-sync", {
        email: form.email.trim(),
        tier: form.tier,
        name: form.name.trim() || undefined,
      });
      toast.success(`Test sync sent: ${form.email} → tier=${form.tier}. Check Kit > Grow > Subscribers in ~30s.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  const tagCount = status?.tags ? Object.keys(status.tags).length : 0;
  const ok = !!status?.ok;

  return (
    <div className="rounded-3xl bg-white ring-1 ring-black/5 p-6" data-testid="admin-convertkit-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Mailing list</div>
          <div className="mt-1 font-heading text-xl text-slate-900 font-medium tracking-tight">ConvertKit (Kit) sync</div>
        </div>
        <div className="flex items-center gap-2">
          {loading ? (
            <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 border-0 rounded-full">Checking…</Badge>
          ) : ok ? (
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0 rounded-full" data-testid="ck-status-ok">
              <CheckCircle2 size={12} className="mr-1.5" /> Connected · {tagCount}/4 tags
            </Badge>
          ) : (
            <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 border-0 rounded-full" data-testid="ck-status-fail">
              <XCircle size={12} className="mr-1.5" /> Not connected
            </Badge>
          )}
          <Button onClick={load} variant="outline" size="sm" className="rounded-full border-slate-300 text-slate-700" data-testid="ck-refresh-btn">
            <RefreshCw size={13} className="mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {!ok && !loading && (
        <div className="mt-4 rounded-2xl bg-rose-50 ring-1 ring-rose-200 px-5 py-3 text-sm text-rose-700">
          {status?.error || "ConvertKit credentials missing. Set CONVERTKIT_API_SECRET in production env vars and redeploy."}
        </div>
      )}

      {ok && status?.tags && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          {Object.entries(status.tags).map(([name, id]) => (
            <div key={id} className="flex items-center justify-between px-3 py-2 rounded-xl bg-slate-50 ring-1 ring-black/5">
              <span className="text-slate-700">{name}</span>
              <span className="font-mono text-slate-400">#{id}</span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 border-t border-black/5 pt-5">
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold mb-3">Send a test tag</div>
        <p className="text-xs text-slate-500 mb-3">Tags an email in Kit without granting a ticket. Use your own address to verify the sync end-to-end.</p>
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
          <Input
            placeholder="you@example.com"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="sm:col-span-5 rounded-xl border-slate-200"
            data-testid="ck-test-email"
          />
          <Input
            placeholder="Name (optional)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="sm:col-span-4 rounded-xl border-slate-200"
            data-testid="ck-test-name"
          />
          <select
            value={form.tier}
            onChange={(e) => setForm({ ...form, tier: e.target.value })}
            className="sm:col-span-2 rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white text-slate-700"
            data-testid="ck-test-tier"
          >
            <option value="full">Full</option>
            <option value="concession">Concession</option>
            <option value="lmic">LMIC</option>
          </select>
          <Button
            onClick={sendTest}
            disabled={testing || !ok}
            className="sm:col-span-1 rounded-xl bg-[#28BBE3] hover:bg-[#1FA8CC] text-white"
            data-testid="ck-test-send-btn"
          >
            {testing ? "…" : "Send"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Attendees() {
  const { user: currentAdmin } = useAuth();
  const [data, setData] = useState({ total: 0, users: [] });
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [granting, setGranting] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantName, setGrantName] = useState("");
  const [bulkGrantOpen, setBulkGrantOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/attendees`, { params: { q, filter, limit: 200 } });
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [q, filter]);

  useEffect(() => { load(); }, [load]);

  const grant = async () => {
    setGranting(true);
    try {
      await api.post("/admin/users/grant", { email: grantEmail, name: grantName || undefined, send_email: true });
      toast.success(`Ticket granted to ${grantEmail}`);
      setGrantOpen(false);
      setGrantEmail(""); setGrantName("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGranting(false);
    }
  };

  const revoke = async (user_id, email) => {
    if (!window.confirm(`Revoke ticket for ${email}?`)) return;
    try {
      await api.post(`/admin/users/${user_id}/revoke`);
      toast.success("Ticket revoked");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteUser = async (user_id, email) => {
    if (!window.confirm(`Permanently delete ${email}?\n\nThis removes their account entirely. Use this for test/spam accounts, not for refunds.`)) return;
    try {
      await api.delete(`/admin/users/${user_id}`);
      toast.success(`Deleted ${email}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resendWelcome = async (user_id, email) => {
    if (!window.confirm(
      `Resend welcome email to ${email}?\n\n` +
      `This will generate a NEW temporary password and email it to them. ` +
      `Any password they previously set themselves will stop working.\n\n` +
      `Use this when someone says "I never got my login email" or "I can't log in".`
    )) return;
    try {
      await api.post(`/admin/users/${user_id}/resend-welcome`);
      toast.success(`Welcome email re-sent to ${email}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const makeAdmin = async (user_id, email) => {
    if (!window.confirm(
      `Promote ${email} to ADMIN?\n\n` +
      `They will gain full access to the admin dashboard, including all attendees, transactions, refunds, and the ability to add/remove other admins.\n\n` +
      `Only do this for people you fully trust.`,
    )) return;
    try {
      await api.post(`/admin/users/${user_id}/make-admin`);
      toast.success(`${email} is now an admin`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const revokeAdmin = async (user_id, email) => {
    if (!window.confirm(
      `Revoke admin rights from ${email}?\n\n` +
      `They will lose access to the admin dashboard but their ticket and account remain intact.`,
    )) return;
    try {
      await api.post(`/admin/users/${user_id}/revoke-admin`);
      toast.success(`Admin rights revoked from ${email}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const inviteAdmin = async () => {
    const email = window.prompt(
      `Invite a new admin by email.\n\n` +
      `If they already have a DFTB26 account, they'll just be promoted to admin. ` +
      `If not, a new account will be created and they'll receive a welcome email with login details.`,
    );
    if (!email) return;
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("That doesn't look like a valid email address.");
      return;
    }
    const name = window.prompt("Optional: full name for the welcome email (leave blank to use the email prefix)", "") || "";
    try {
      await api.post(`/admin/admins/invite`, {
        email: trimmed,
        name: name.trim(),
        send_email: true,
      });
      toast.success(`${trimmed} invited as admin and emailed their login.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const editEmail = async (user_id, oldEmail) => {
    const newEmail = window.prompt(
      `Update email address for this attendee.\n\n` +
      `Current: ${oldEmail}\n\n` +
      `Enter the corrected email below. A welcome email with a fresh login will be sent to the new address.`,
      oldEmail,
    );
    if (newEmail === null) return; // cancelled
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed || trimmed === oldEmail.toLowerCase()) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("That doesn't look like a valid email address.");
      return;
    }
    try {
      await api.post(`/admin/users/${user_id}/update-email`, {
        new_email: trimmed,
        resend_welcome: true,
      });      toast.success(`Email updated to ${trimmed} — welcome email re-sent.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cleanupTestUsers = async () => {
    if (!window.confirm("Bulk-delete all test/fixture users?\n\nThis removes everyone whose email ends in @example.com or matches a known test pattern (e.g. no-deliver-test+..., test_user_...). Admins are never affected.")) return;
    try {
      const { data } = await api.post(`/admin/users/cleanup-test`);
      if (data.deleted === 0) {
        toast.success("No test users to remove - you're clean.");
      } else {
        toast.success(`Removed ${data.deleted} test users.`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reissueStripeInvoice = async (user_id, email) => {
    if (!user_id) return;
    if (!window.confirm(
      `Reissue the Stripe invoice for ${email}?\n\n` +
      `Stripe will re-send the branded invoice PDF (from noreply@stripe.com) directly to the email on file at Stripe. ` +
      `Use this when a customer asks for another copy of the invoice for expenses / their finance team.`
    )) return;
    try {
      await api.post(`/admin/users/${user_id}/reissue-invoice`);
      toast.success(`Stripe invoice re-sent to ${email}.`);
    } catch (e) {
      toast.error(formatApiError(e) || "Reissue failed.");
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await fetch(`${API}/admin/attendees.csv`, { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "dftb26_attendees.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("CSV export failed"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-attendees">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email or name"
            className="rounded-2xl bg-white pl-10 py-5 border-slate-200" data-testid="attendees-search-input" />
        </div>
        <div className="flex rounded-full bg-white ring-1 ring-black/5 p-1 text-sm">
          {["all", "ticket", "no_ticket"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full transition ${filter === f ? "bg-[#28BBE3] text-white" : "text-slate-600"}`}
              data-testid={`attendees-filter-${f}`}>
              {f === "all" ? "All" : f === "ticket" ? "With ticket" : "No ticket"}
            </button>
          ))}
        </div>
        <Button onClick={downloadCsv} variant="outline" className="rounded-full border-slate-300" data-testid="attendees-export-csv-btn">
          <Download size={15} className="mr-2" /> Export CSV
        </Button>
        <Button onClick={cleanupTestUsers} variant="outline"
                className="rounded-full border-slate-300 text-slate-600 hover:text-rose-600"
                data-testid="attendees-cleanup-test-btn">
          <Trash2 size={14} className="mr-2" /> Clean up test users
        </Button>
        <Button onClick={inviteAdmin} variant="outline" className="rounded-full border-slate-300" data-testid="invite-admin-btn">
          <ShieldCheck size={14} className="mr-2" /> Invite admin
        </Button>
        <Button
          onClick={() => setBulkGrantOpen(true)}
          variant="outline"
          className="rounded-full border-[#28BBE3]/40 text-[#0F8FB0] hover:bg-[#28BBE3]/10"
          data-testid="bulk-grant-open-btn"
        >
          <Upload size={14} className="mr-2" /> Bulk grant
        </Button>
        <BulkGrantDialog
          open={bulkGrantOpen}
          onOpenChange={setBulkGrantOpen}
          onComplete={load}
        />
        <Dialog open={grantOpen} onOpenChange={setGrantOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white" data-testid="attendees-grant-open-btn">
              <Plus size={15} className="mr-2" /> Grant ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">Grant a complimentary ticket</DialogTitle>
              <DialogDescription>The user will receive an email with their login info. We'll create the account if they don't have one yet.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Email</Label>
                <Input value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)}
                  className="mt-2 rounded-xl border-slate-200 py-5" placeholder="attendee@hospital.org"
                  data-testid="grant-email-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Name (optional)</Label>
                <Input value={grantName} onChange={(e) => setGrantName(e.target.value)}
                  className="mt-2 rounded-xl border-slate-200 py-5" placeholder="Dr. Jane Doe"
                  data-testid="grant-name-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={grant} disabled={!grantEmail || granting}
                className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white" data-testid="grant-submit-btn">
                {granting ? "Granting…" : "Grant & email"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-3xl bg-white ring-1 ring-black/5 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
            <tr>
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Role · Hospital</th>
              <th className="px-5 py-3">Country</th>
              <th className="px-5 py-3">Joined</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {data.users.map((u) => (
              <tr key={u.user_id} className="hover:bg-slate-50/60" data-testid={`attendee-row-${u.user_id}`}>
                <td className="px-5 py-3 font-medium text-slate-900">{u.name || "-"}</td>
                <td className="px-5 py-3 text-slate-600">{u.email}</td>
                <td className="px-5 py-3">
                  {u.role === "admin" ? (
                    <Badge className="bg-slate-900 text-white hover:bg-slate-900 border-0 rounded-full" data-testid={`admin-badge-${u.user_id}`}>
                      <ShieldCheck size={11} className="mr-1" /> Admin
                    </Badge>
                  ) : u.has_ticket ? (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0 rounded-full">
                        <CheckCircle2 size={11} className="mr-1" /> Ticket
                      </Badge>
                      {u.ticket_tier && (
                        <Badge
                          className={`rounded-full border-0 text-[10px] ${
                            u.ticket_tier === "full"
                              ? "bg-sky-100 text-sky-700 hover:bg-sky-100"
                              : u.ticket_tier === "concession"
                              ? "bg-violet-100 text-violet-700 hover:bg-violet-100"
                              : u.ticket_tier === "lmic"
                              ? "bg-amber-100 text-amber-800 hover:bg-amber-100"
                              : u.ticket_tier === "face_to_face"
                              ? "bg-orange-100 text-orange-800 hover:bg-orange-100"
                              : "bg-slate-200 text-slate-600 hover:bg-slate-200"
                          }`}
                          data-testid={`tier-badge-${u.user_id}`}
                        >
                          {u.ticket_tier === "full" ? "Full"
                            : u.ticket_tier === "concession" ? "Concession"
                            : u.ticket_tier === "lmic" ? "LMIC"
                            : u.ticket_tier === "face_to_face" ? "F2F"
                            : "Admin grant"}
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <Badge variant="outline" className="rounded-full text-slate-500">
                      <XCircle size={11} className="mr-1" /> None
                    </Badge>
                  )}
                </td>
                <td className="px-5 py-3 text-slate-600 text-xs max-w-[280px]">
                  {(u.profile?.job_role || u.profile?.hospital) ? (
                    <div data-testid={`attendee-profile-${u.user_id}`}>
                      <div className="font-medium text-slate-900 truncate">
                        {u.profile?.job_role === "Other" && u.profile?.job_role_other
                          ? u.profile.job_role_other
                          : u.profile?.job_role || "—"}
                      </div>
                      {u.profile?.hospital && (
                        <div className="text-slate-500 truncate">{u.profile.hospital}</div>
                      )}
                    </div>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-5 py-3 text-slate-500 font-mono text-xs">
                  {u.profile?.country || <span className="text-slate-300">—</span>}
                </td>
                <td className="px-5 py-3 text-slate-500 font-mono text-xs">{(u.created_at || "").slice(0, 10)}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  {u.role === "admin" ? (
                    <div className="inline-flex items-center gap-3">
                      {u.user_id === currentAdmin?.user_id ? (
                        <span className="text-xs text-slate-400 italic">You</span>
                      ) : (
                        <button onClick={() => revokeAdmin(u.user_id, u.email)}
                          className="text-xs text-rose-600 hover:underline inline-flex items-center gap-1"
                          data-testid={`revoke-admin-${u.user_id}`}>
                          <ShieldOff size={11} /> Revoke admin
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-3">
                      <button onClick={() => editEmail(u.user_id, u.email)}
                        className="text-xs text-slate-500 hover:text-[#28BBE3] hover:underline" data-testid={`edit-email-${u.user_id}`}>
                        Edit email
                      </button>
                      <button onClick={() => resendWelcome(u.user_id, u.email)}
                        className="text-xs text-slate-500 hover:text-[#28BBE3] hover:underline" data-testid={`resend-${u.user_id}`}>
                        Resend welcome
                      </button>
                      {(u.ticket_tier === "full" || u.ticket_tier === "concession") && (
                        <button onClick={() => reissueStripeInvoice(u.user_id, u.email)}
                          className="text-xs text-slate-500 hover:text-[#28BBE3] hover:underline"
                          data-testid={`reissue-invoice-${u.user_id}`}
                          title="Ask Stripe to re-send the branded invoice PDF">
                          Reissue invoice
                        </button>
                      )}
                      <button onClick={() => makeAdmin(u.user_id, u.email)}
                        className="text-xs text-slate-500 hover:text-[#28BBE3] hover:underline" data-testid={`make-admin-${u.user_id}`}>
                        Make admin
                      </button>
                      {u.has_ticket && (
                        <button onClick={() => revoke(u.user_id, u.email)}
                          className="text-xs text-rose-600 hover:underline" data-testid={`revoke-${u.user_id}`}>
                          Revoke
                        </button>
                      )}
                      <button onClick={() => deleteUser(u.user_id, u.email)}
                        className="text-xs text-slate-400 hover:text-rose-600 hover:underline" data-testid={`delete-${u.user_id}`}>
                        Delete
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {data.users.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400">No attendees match this filter.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
      <div className="text-xs text-slate-500">Showing {data.users.length} of {data.total} total</div>
    </div>
  );
}

function SessionForm({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    day_index: initial?.day_index || 1,
    day_label: initial?.day_label || "Day 1 - Monday 22 June 2026",
    time: initial?.time || "09:00",
    title: initial?.title || "",
    speaker: initial?.speaker || "",
    description: initial?.description || "",
    track: initial?.track || "",
    cloudflare_uid: initial?.cloudflare_uid || "",
    iframe_url: initial?.iframe_url || "",
    hls_url: initial?.hls_url || "",
    is_featured: initial?.is_featured || false,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) await api.put(`/admin/sessions/${initial.session_id}`, form);
      else await api.post("/admin/sessions", form);
      toast.success(isEdit ? "Session updated" : "Session created");
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-3 pt-2">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Day</Label>
          <Input type="number" min="1" max="10" value={form.day_index} onChange={(e) => setForm({...form, day_index: parseInt(e.target.value) || 1})}
            className="mt-2 rounded-xl border-slate-200" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Time</Label>
          <Input value={form.time} onChange={(e) => setForm({...form, time: e.target.value})}
            placeholder="09:00" className="mt-2 rounded-xl border-slate-200" />
        </div>
        <div className="col-span-1 flex items-end pb-1.5">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Switch checked={form.is_featured} onCheckedChange={(v) => setForm({...form, is_featured: v})} data-testid="session-featured-switch" />
            Featured (live now)
          </label>
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Day label</Label>
        <Input value={form.day_label} onChange={(e) => setForm({...form, day_label: e.target.value})}
          className="mt-2 rounded-xl border-slate-200" placeholder="Day 1 - Monday 22 June 2026" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Title</Label>
        <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
          className="mt-2 rounded-xl border-slate-200" placeholder="Opening keynote" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Speaker</Label>
        <Input value={form.speaker} onChange={(e) => setForm({...form, speaker: e.target.value})}
          className="mt-2 rounded-xl border-slate-200" placeholder="Dr. Tessa Davis" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Track</Label>
        <Input value={form.track} onChange={(e) => setForm({...form, track: e.target.value})}
          data-testid="session-track-input"
          className="mt-2 rounded-xl border-slate-200" placeholder='e.g. "Keynote 1: Basics", "Concurrent 1: …", or "Break"' />
        <p className="text-[11px] text-slate-400 mt-1.5">Sessions are grouped by track on the public schedule. Use the same prefix (Keynote / Concurrent N / Break) across sessions in the same slot.</p>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Description (optional)</Label>
        <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
          data-testid="session-description-input"
          rows={2} className="mt-2 rounded-xl border-slate-200" placeholder="Short blurb shown under the session on the schedule." />
      </div>
      <div className="pt-3 border-t border-black/5">
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold mb-3">Cloudflare Stream - paste one</div>
        <div className="space-y-2">
          <div>
            <Label className="text-xs text-slate-500">Video UID (preferred)</Label>
            <Input value={form.cloudflare_uid} onChange={(e) => setForm({...form, cloudflare_uid: e.target.value})}
              className="mt-1 rounded-xl border-slate-200 font-mono text-xs" placeholder="ea95132c15732412d22c1476fa83f27a" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">…or full iframe URL</Label>
            <Input value={form.iframe_url} onChange={(e) => setForm({...form, iframe_url: e.target.value})}
              className="mt-1 rounded-xl border-slate-200 font-mono text-xs" placeholder="https://customer-xxxxx.cloudflarestream.com/<uid>/iframe" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">…or HLS manifest URL</Label>
            <Input value={form.hls_url} onChange={(e) => setForm({...form, hls_url: e.target.value})}
              className="mt-1 rounded-xl border-slate-200 font-mono text-xs" placeholder="https://customer-xxxxx.cloudflarestream.com/<uid>/manifest/video.m3u8" />
          </div>
        </div>
      </div>
      <div className="flex justify-end pt-3">
        <Button onClick={save} disabled={saving || !form.title}
          className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white" data-testid="session-save-btn">
          {saving ? "Saving…" : isEdit ? "Save changes" : "Create session"}
        </Button>
      </div>
    </div>
  );
}

function Sessions() {
  const [sessions, setSessions] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [dayFilter, setDayFilter] = useState("all");
  const [trackFilter, setTrackFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/sessions");
      setSessions(r.data.sessions || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const trackOptions = React.useMemo(() => {
    const set = new Set();
    sessions.forEach((s) => { if (s.track) set.add(s.track); });
    return ["all", ...[...set].sort()];
  }, [sessions]);

  const dayOptions = React.useMemo(() => {
    const set = new Set();
    sessions.forEach((s) => set.add(s.day_index));
    return ["all", ...[...set].sort((a, b) => a - b)];
  }, [sessions]);

  const filteredSessions = React.useMemo(() => {
    return sessions.filter((s) => {
      if (dayFilter !== "all" && s.day_index !== Number(dayFilter)) return false;
      if (trackFilter !== "all" && s.track !== trackFilter) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const hay = `${s.title || ""} ${s.speaker || ""} ${s.track || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [sessions, dayFilter, trackFilter, search]);

  const loadLastSync = useCallback(async () => {
    try {
      const r = await api.get("/admin/programme/sync/last");
      setLastSync(r.data.last_sync);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { load(); loadLastSync(); }, [load, loadLastSync]);

  const resync = async () => {
    if (!window.confirm(
      "Pull the latest programme from app.dftb26.com?\n\n" +
      "This will replace the current programme with the live one. Your attached " +
      "Vimeo / iframe URLs will be preserved for sessions whose day + time + title match."
    )) return;
    setSyncing(true);
    try {
      const r = await api.post("/admin/programme/sync");
      const s = r.data;
      toast.success(
        `Synced • ${s.total_written} sessions • +${s.added} new / -${s.removed} removed` +
        (s.rekeyed_lost_video ? ` • ⚠ ${s.rekeyed_lost_video} lost video link` : "")
      );
      await load();
      await loadLastSync();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncing(false); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete "${s.title}"?`)) return;
    try {
      await api.delete(`/admin/sessions/${s.session_id}`);
      toast.success("Session deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setFeatured = async (s) => {
    try {
      await api.post(`/admin/sessions/${s.session_id}/feature`);
      toast.success(`"${s.title}" is now featured live`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-sessions">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-heading text-2xl tracking-tight font-medium text-slate-900">Conference sessions</h2>
          <p className="text-sm text-slate-500 mt-1">Paste a Cloudflare Stream video UID for each session - the player embeds automatically.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={resync}
            disabled={syncing}
            className="rounded-full border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="programme-resync-btn"
          >
            <RefreshCw size={14} className={`mr-2 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Re-sync from app.dftb26.com"}
          </Button>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button onClick={() => setEditing(null)} className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white" data-testid="session-create-btn">
                <Plus size={15} className="mr-2" /> New session
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading tracking-tight">{editing ? "Edit session" : "New session"}</DialogTitle>
                <DialogDescription>Set day, time, speaker, and optionally a Cloudflare Stream video.</DialogDescription>
              </DialogHeader>
              <SessionForm initial={editing} onClose={() => setOpen(false)} onSaved={load} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {lastSync && (
        <div
          className="rounded-2xl bg-slate-50 ring-1 ring-black/5 px-5 py-3 text-xs text-slate-600 flex flex-wrap items-center gap-x-4 gap-y-1"
          data-testid="programme-last-sync"
        >
          <span className="font-semibold text-slate-700">
            Last sync: {new Date(lastSync.finished_at).toLocaleString()}
          </span>
          <span>Trigger: <span className="font-mono">{lastSync.trigger}</span></span>
          {lastSync.ok ? (
            <>
              <span>Written: <strong>{lastSync.total_written}</strong></span>
              <span>Added: <strong>{lastSync.added}</strong></span>
              <span>Removed: <strong>{lastSync.removed}</strong></span>
              <span>Blocked: <strong>{lastSync.total_blocked}</strong></span>
              {lastSync.rekeyed_lost_video > 0 && (
                <span className="text-amber-700 font-semibold">
                  ⚠ {lastSync.rekeyed_lost_video} re-keyed → re-attach video
                </span>
              )}
            </>
          ) : (
            <span className="text-rose-600">Error: {lastSync.error}</span>
          )}
          <span className="text-slate-400 ml-auto">Auto-syncs every 24h</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 px-1" data-testid="sessions-filter-bar">
        <Input
          placeholder="Search title, speaker, track…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-full border-slate-200 max-w-xs"
          data-testid="sessions-search-input"
        />
        <select
          value={dayFilter}
          onChange={(e) => setDayFilter(e.target.value)}
          data-testid="sessions-day-filter"
          className="rounded-full border border-slate-200 px-4 py-2 text-sm bg-white text-slate-700"
        >
          {dayOptions.map((d) => (
            <option key={d} value={d}>{d === "all" ? "All days" : `Day ${d}`}</option>
          ))}
        </select>
        <select
          value={trackFilter}
          onChange={(e) => setTrackFilter(e.target.value)}
          data-testid="sessions-track-filter"
          className="rounded-full border border-slate-200 px-4 py-2 text-sm bg-white text-slate-700 max-w-xs truncate"
        >
          {trackOptions.map((t) => (
            <option key={t} value={t}>{t === "all" ? "All tracks" : t}</option>
          ))}
        </select>
        <span className="text-xs text-slate-500 ml-1">
          Showing <strong>{filteredSessions.length}</strong> of {sessions.length} sessions
        </span>
      </div>

      <div className="rounded-3xl bg-white ring-1 ring-black/5 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
            <tr>
              <th className="px-5 py-3">Day</th>
              <th className="px-5 py-3">Time</th>
              <th className="px-5 py-3">Track</th>
              <th className="px-5 py-3">Title</th>
              <th className="px-5 py-3">Speaker</th>
              <th className="px-5 py-3">Video</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {filteredSessions.map((s) => (
              <tr key={s.session_id} className="hover:bg-slate-50/60" data-testid={`session-row-${s.session_id}`}>
                <td className="px-5 py-3 text-slate-700 font-mono text-xs">D{s.day_index}</td>
                <td className="px-5 py-3 text-slate-700 font-mono text-xs">{s.time}</td>
                <td className="px-5 py-3 text-slate-600 text-xs">
                  {s.track ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 text-slate-700">{s.track}</span>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-5 py-3 font-medium text-slate-900">
                  {s.is_featured && <Star size={12} className="inline mr-1.5 fill-[#28BBE3] text-[#28BBE3]" />}
                  {s.title}
                </td>
                <td className="px-5 py-3 text-slate-500">{s.speaker || "-"}</td>
                <td className="px-5 py-3">
                  {s.computed_iframe_url || s.hls_url ? (
                    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-0 rounded-full">
                      <Video size={11} className="mr-1" /> Linked
                    </Badge>
                  ) : <span className="text-xs text-slate-400">-</span>}
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex items-center gap-2">
                    {!s.is_featured && (
                      <button onClick={() => setFeatured(s)} className="text-xs text-slate-500 hover:text-[#28BBE3]"
                        data-testid={`feature-${s.session_id}`}>Feature</button>
                    )}
                    <button onClick={() => { setEditing(s); setOpen(true); }} className="text-slate-500 hover:text-slate-900"
                      data-testid={`edit-${s.session_id}`}>
                      <Edit size={14} />
                    </button>
                    <button onClick={() => del(s)} className="text-rose-500 hover:text-rose-700"
                      data-testid={`delete-${s.session_id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredSessions.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400">{sessions.length === 0 ? "No sessions yet." : "No sessions match the current filters."}</td></tr>}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function LMIC() {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [applications, setApplications] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/lmic?status=${statusFilter}`);
      setApplications(r.data.applications || []);
      setCounts(r.data.counts || {});
    } catch (e) { toast.error(formatApiError(e)); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const decide = async (app, decision) => {
    const notes = decision === "reject"
      ? window.prompt(`Reject "${app.full_name}" — optional note (visible only to admins):`, "")
      : window.prompt(`Approve free access for "${app.full_name}" (${app.email}) — optional note:`, "");
    if (notes === null) return;  // cancelled
    setActingId(app.application_id);
    try {
      await api.post(`/admin/lmic/${app.application_id}/${decision}`, { notes, send_email: true });
      toast.success(decision === "approve"
        ? `Granted free access to ${app.email}`
        : `Rejected application from ${app.full_name}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setActingId(null); }
  };

  return (
    <div className="space-y-5" data-testid="admin-lmic">
      <div>
        <h2 className="font-heading text-2xl tracking-tight font-medium text-slate-900">LMIC free-access applications</h2>
        <p className="text-sm text-slate-500 mt-1">Review applications manually. Approving grants the user a free ticket and emails them a login.</p>
      </div>

      <div className="flex gap-2">
        {[
          { v: "pending", label: `Pending (${counts.pending || 0})` },
          { v: "approved", label: `Approved (${counts.approved || 0})` },
          { v: "rejected", label: `Rejected (${counts.rejected || 0})` },
        ].map((opt) => (
          <button
            key={opt.v}
            onClick={() => setStatusFilter(opt.v)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-colors ${
              statusFilter === opt.v
                ? "bg-[#28BBE3] text-white"
                : "bg-white ring-1 ring-black/5 text-slate-600 hover:bg-slate-50"
            }`}
            data-testid={`lmic-filter-${opt.v}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="rounded-3xl bg-white ring-1 ring-black/5 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
            <tr>
              <th className="px-5 py-3">Applied</th>
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Country</th>
              <th className="px-5 py-3">Hospital / Role</th>
              <th className="px-5 py-3">Reason</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {applications.map((a) => (
              <tr key={a.application_id} className="hover:bg-slate-50/60 align-top"
                  data-testid={`lmic-row-${a.application_id}`}>
                <td className="px-5 py-4 font-mono text-xs text-slate-500 whitespace-nowrap">
                  {(a.created_at || "").slice(0, 10)}
                </td>
                <td className="px-5 py-4 font-medium text-slate-900">{a.full_name}</td>
                <td className="px-5 py-4 text-slate-700 break-all">{a.email}</td>
                <td className="px-5 py-4 text-slate-700">{a.country}</td>
                <td className="px-5 py-4 text-slate-700">{a.hospital_role}</td>
                <td className="px-5 py-4 text-slate-600 text-xs leading-relaxed max-w-xs">{a.reason}</td>
                <td className="px-5 py-4 text-right whitespace-nowrap">
                  {a.status === "pending" ? (
                    <div className="inline-flex items-center gap-2">
                      <button
                        disabled={actingId === a.application_id}
                        onClick={() => decide(a, "approve")}
                        className="px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50"
                        data-testid={`lmic-approve-${a.application_id}`}
                      >
                        <CheckCircle2 size={12} className="inline mr-1" /> Approve
                      </button>
                      <button
                        disabled={actingId === a.application_id}
                        onClick={() => decide(a, "reject")}
                        className="px-3 py-1.5 rounded-full bg-white ring-1 ring-rose-200 text-rose-600 hover:bg-rose-50 text-xs font-semibold disabled:opacity-50"
                        data-testid={`lmic-reject-${a.application_id}`}
                      >
                        <XCircle size={12} className="inline mr-1" /> Reject
                      </button>
                    </div>
                  ) : (
                    <Badge className={`rounded-full border-0 ${
                      a.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                    }`}>
                      {a.status}
                    </Badge>
                  )}
                </td>
              </tr>
            ))}
            {applications.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-16 text-center text-slate-400">No {statusFilter} applications.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function Transactions() {
  const [txs, setTxs] = useState([]);
  const [busy, setBusy] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    api.get("/admin/transactions").then((r) => setTxs(r.data.transactions || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const reconcileOne = async (sid, email) => {
    setBusy((p) => ({ ...p, [sid]: true }));
    try {
      const r = await api.post(`/admin/transactions/${sid}/reconcile`);
      if (r.data.granted) {
        toast.success(`Ticket granted for ${r.data.email || email || sid.slice(0, 14)}`);
      } else if (r.data.ok && !r.data.granted) {
        toast.info(r.data.reason || "Already reconciled");
      } else {
        toast.warning(r.data.reason || "Could not grant ticket");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((p) => ({ ...p, [sid]: false }));
    }
  };

  const forceGrant = async (sid, email) => {
    if (!window.confirm(
      `Force re-grant for ${email || sid.slice(0, 14)}?\n\n` +
      `Use this if the transaction is marked 'paid' but the buyer is missing from Attendees / didn't get the welcome email. Re-runs grant + email send.\n\n` +
      `Safe to run multiple times — won't double-charge or duplicate accounts.`
    )) return;
    setBusy((p) => ({ ...p, [sid]: true }));
    try {
      const r = await api.post(`/admin/transactions/${sid}/force-grant`);
      if (r.data.granted) toast.success(`Ticket granted + welcome email sent to ${email || sid.slice(0, 14)}`);
      else toast.warning("Force-grant ran but no ticket was granted — check backend logs");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((p) => ({ ...p, [sid]: false }));
    }
  };

  const resendInvoice = async (sid, email) => {
    if (!window.confirm(
      `Re-email the Stripe invoice for ${email || sid.slice(0, 14)}?\n\n` +
      `Stripe will resend the same hosted PDF to the buyer's email on file.`,
    )) return;
    setBusy((p) => ({ ...p, [sid]: true }));
    try {
      await api.post(`/admin/transactions/${sid}/resend-invoice`);
      toast.success(`Invoice re-sent to ${email || sid.slice(0, 14)}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((p) => ({ ...p, [sid]: false }));
    }
  };

  const reconcileAll = async () => {
    if (!window.confirm(
      "Reconcile ALL unpaid transactions?\n\n" +
      "This re-checks each session against Stripe. Anything genuinely paid will get its ticket granted and a welcome email sent. Anything Stripe still reports as unpaid is left alone.\n\n" +
      "Safe to run multiple times — already-granted transactions are skipped."
    )) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/transactions/reconcile-all");
      toast.success(`Checked ${r.data.checked} transactions · ${r.data.granted} tickets granted`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const syncRefunds = async () => {
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/transactions/sync-refunds");
      toast.success(
        `Refunds synced from Stripe · ${r.data.with_refunds} transactions refunded · £${(r.data.total_refunded_gbp || 0).toFixed(2)} total`
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const toggleTest = async (sid, currentlyTest) => {
    setBusy((p) => ({ ...p, [sid]: true }));
    try {
      const r = await api.post(`/admin/transactions/${sid}/toggle-test`);
      toast.success(r.data.is_test ? "Marked as test (excluded from revenue)" : "Marked as real (included in revenue)");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((p) => ({ ...p, [sid]: false }));
    }
  };

  const unpaidCount = txs.filter((t) => t.payment_status !== "paid").length;
  const testCount = txs.filter((t) => t.is_test).length;
  const refundedCount = txs.filter((t) => (t.amount_refunded || 0) > 0).length;

  const filteredTxs = (() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return txs;
    return txs.filter((t) =>
      (t.email || "").toLowerCase().includes(needle)
      || (t.name || "").toLowerCase().includes(needle)
      || (t.session_id || "").toLowerCase().includes(needle)
      || (t.package_id || "").toLowerCase().includes(needle)
    );
  })();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between px-1 flex-wrap gap-2">
        <div className="text-sm text-slate-500">
          {q.trim() ? `${filteredTxs.length} of ${txs.length}` : txs.length} transaction{txs.length === 1 ? "" : "s"}
          {" · "}<span className={unpaidCount ? "text-amber-700 font-semibold" : ""}>{unpaidCount} unpaid</span>
          {testCount > 0 && <> · <span className="text-slate-400">{testCount} test</span></>}
          {refundedCount > 0 && <> · <span className="text-rose-600">{refundedCount} refunded</span></>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search email, name, session…"
              className="pl-9 pr-8 h-9 w-[260px] rounded-full bg-white border-slate-200"
              data-testid="transactions-search-input"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-xs"
                aria-label="Clear search"
                data-testid="transactions-search-clear"
              >
                ✕
              </button>
            )}
          </div>
          <Button onClick={syncRefunds} disabled={bulkBusy}
            variant="outline"
            className="rounded-full border-slate-300 text-slate-700"
            data-testid="sync-refunds-btn">
            {bulkBusy ? "Syncing…" : "Sync refunds from Stripe"}
          </Button>
          {unpaidCount > 0 && (
            <Button onClick={reconcileAll} disabled={bulkBusy}
              className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white"
              data-testid="reconcile-all-btn">
              {bulkBusy ? "Reconciling…" : `Reconcile all ${unpaidCount} unpaid`}
            </Button>
          )}
        </div>
      </div>

      <div data-testid="admin-transactions" className="rounded-3xl bg-white ring-1 ring-black/5 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
            <tr>
              <th className="px-5 py-3">Date</th>
              <th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Amount</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Session</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {filteredTxs.map((t) => {
              const grossCents = t.amount_total || (t.amount || 0) * 100;
              const refundedCents = t.amount_refunded || 0;
              const netCents = Math.max(grossCents - refundedCents, 0);
              const isTest = !!t.is_test;
              return (
                <tr key={t.session_id} className={`hover:bg-slate-50/60 ${isTest ? "bg-slate-50/40 opacity-70" : ""}`}>
                  <td className="px-5 py-3 font-mono text-xs text-slate-500">{(t.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-5 py-3 text-slate-700">{t.email || "-"}</td>
                  <td className="px-5 py-3 font-medium">
                    £{(netCents / 100).toFixed(2)}
                    {refundedCents > 0 && (
                      <span className="ml-1.5 text-xs text-rose-500" title={`£${(refundedCents/100).toFixed(2)} refunded of £${(grossCents/100).toFixed(2)} charged`}>
                        (£{(refundedCents/100).toFixed(2)} refunded)
                      </span>
                    )}
                    <span className="text-xs text-slate-400 uppercase ml-1">{t.currency}</span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge className={`rounded-full border-0 ${t.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"} hover:opacity-90`}>
                        {t.payment_status}
                      </Badge>
                      {isTest && (
                        <Badge className="rounded-full border-0 bg-slate-200 text-slate-600 hover:bg-slate-200 text-[10px]">
                          TEST
                        </Badge>
                      )}
                      {refundedCents > 0 && refundedCents >= grossCents && (
                        <Badge className="rounded-full border-0 bg-rose-100 text-rose-700 hover:bg-rose-100 text-[10px]">
                          REFUNDED
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 font-mono text-[10px] text-slate-400 truncate max-w-[200px]">{t.session_id}</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-3">
                      {t.payment_status !== "paid" && t.email && (
                        <button onClick={() => reconcileOne(t.session_id, t.email)}
                          disabled={!!busy[t.session_id]}
                          className="text-xs text-[#28BBE3] hover:underline disabled:opacity-50"
                          data-testid={`reconcile-${t.session_id}`}>
                          {busy[t.session_id] ? "…" : "Reconcile"}
                        </button>
                      )}
                      {t.payment_status === "paid" && t.email && (
                        <button onClick={() => forceGrant(t.session_id, t.email)}
                          disabled={!!busy[t.session_id]}
                          className="text-xs text-slate-400 hover:text-[#28BBE3] hover:underline disabled:opacity-50"
                          data-testid={`force-grant-${t.session_id}`}
                          title="Re-run welcome email + ticket grant for this paid transaction">
                          {busy[t.session_id] ? "…" : "Resend email"}
                        </button>
                      )}
                      {t.payment_status === "paid" && (
                        <button onClick={() => resendInvoice(t.session_id, t.email)}
                          disabled={!!busy[t.session_id]}
                          className="text-xs text-slate-400 hover:text-[#28BBE3] hover:underline disabled:opacity-50"
                          data-testid={`resend-invoice-${t.session_id}`}
                          title="Re-email the Stripe hosted invoice PDF to the buyer">
                          {busy[t.session_id] ? "…" : "Resend invoice"}
                        </button>
                      )}
                      <button onClick={() => toggleTest(t.session_id, isTest)}
                        disabled={!!busy[t.session_id]}
                        className="text-xs text-slate-400 hover:text-slate-700 hover:underline disabled:opacity-50"
                        data-testid={`toggle-test-${t.session_id}`}
                        title={isTest ? "Include in revenue stats" : "Exclude from revenue stats (mark as test)"}>
                        {isTest ? "Mark real" : "Mark test"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredTxs.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-slate-400">
                  {q.trim() ? `No transactions match "${q}".` : "No transactions yet."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function Rooms() {
  const [rooms, setRooms] = useState([]);
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/rooms");
      setRooms(r.data.rooms || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (id, field, value) => {
    setEditing((p) => ({ ...p, [id]: { ...(p[id] || {}), [field]: value } }));
  };

  const save = async (room) => {
    const patch = editing[room.id] || {};
    if (Object.keys(patch).length === 0) return;
    setSaving((p) => ({ ...p, [room.id]: true }));
    try {
      await api.put(`/admin/rooms/${room.id}`, patch);
      toast.success(`"${room.name}" updated`);
      setEditing((p) => { const c = { ...p }; delete c[room.id]; return c; });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving((p) => ({ ...p, [room.id]: false })); }
  };

  const setDefault = async (room) => {
    try {
      await api.put(`/admin/rooms/${room.id}`, { is_default: true });
      toast.success(`"${room.name}" is now the default tab`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-rooms">
      <div>
        <h2 className="font-heading text-2xl tracking-tight font-medium text-slate-900">Live streaming rooms</h2>
        <p className="text-sm text-slate-500 mt-1">
          Edit the Vimeo embed URL for each conference room. Paste the <span className="font-mono text-xs">src</span> attribute from the embed code shared by the production team.
        </p>
      </div>

      <div className="space-y-4">
        {rooms.map((room) => {
          const draft = editing[room.id] || {};
          const dirty = Object.keys(draft).length > 0;
          return (
            <div key={room.id} className="rounded-3xl bg-white ring-1 ring-black/5 p-6 sm:p-8" data-testid={`room-card-${room.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-[#28BBE3]/10 flex items-center justify-center">
                    <Radio className="text-[#28BBE3]" size={18} strokeWidth={1.8} />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">{room.id}</div>
                    <div className="font-heading text-lg font-bold text-slate-900">{room.name}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {room.is_default ? (
                    <Badge className="bg-[#28BBE3]/10 text-[#28BBE3] hover:bg-[#28BBE3]/10 border-0 rounded-full">
                      <Star size={11} className="mr-1 fill-[#28BBE3]" /> Default tab
                    </Badge>
                  ) : (
                    <button onClick={() => setDefault(room)} className="text-xs text-slate-500 hover:text-[#28BBE3]"
                      data-testid={`room-set-default-${room.id}`}>Set as default</button>
                  )}
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Display name</Label>
                  <Input
                    value={draft.name ?? room.name}
                    onChange={(e) => setField(room.id, "name", e.target.value)}
                    className="mt-2 rounded-xl border-slate-200 py-5"
                    data-testid={`room-name-${room.id}`}
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Subtitle</Label>
                  <Input
                    value={draft.subtitle ?? room.subtitle ?? ""}
                    onChange={(e) => setField(room.id, "subtitle", e.target.value)}
                    className="mt-2 rounded-xl border-slate-200 py-5"
                    data-testid={`room-subtitle-${room.id}`}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Vimeo embed URL</Label>
                  <Input
                    value={draft.vimeo_embed_url ?? room.vimeo_embed_url ?? ""}
                    onChange={(e) => setField(room.id, "vimeo_embed_url", e.target.value)}
                    className="mt-2 rounded-xl border-slate-200 py-5 font-mono text-xs"
                    placeholder="https://vimeo.com/event/XXXXXXX/embed/XXXXXXXXXX"
                    data-testid={`room-vimeo-url-${room.id}`}
                  />
                  <p className="mt-1.5 text-[11px] text-slate-400">
                    From the embed code, paste only the URL inside <span className="font-mono">src="..."</span>.
                  </p>
                </div>
              </div>

              <div className="mt-5 flex items-center justify-end gap-3">
                {dirty && (
                  <button
                    onClick={() => setEditing((p) => { const c = { ...p }; delete c[room.id]; return c; })}
                    className="text-sm text-slate-500 hover:text-slate-700"
                    data-testid={`room-cancel-${room.id}`}
                  >
                    Discard
                  </button>
                )}
                <Button
                  onClick={() => save(room)}
                  disabled={!dirty || saving[room.id]}
                  className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white disabled:opacity-40"
                  data-testid={`room-save-${room.id}`}
                >
                  <Save size={14} className="mr-2" />
                  {saving[room.id] ? "Saving..." : "Save changes"}
                </Button>
              </div>
            </div>
          );
        })}
        {rooms.length === 0 && (
          <div className="rounded-3xl bg-white ring-1 ring-black/5 p-10 text-center text-slate-400">
            No rooms configured yet.
          </div>
        )}
      </div>
    </div>
  );
}

function slugifyTrack(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function SessionLinks() {
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDay, setActiveDay] = useState(1);
  const [drafts, setDrafts] = useState({});
  const [videoDrafts, setVideoDrafts] = useState({});
  const [audioDrafts, setAudioDrafts] = useState({});
  const [pickState, setPickState] = useState({}); // {session_id: bool}
  const [busy, setBusy] = useState({});
  const [pickBusy, setPickBusy] = useState({});
  // Block-level Q+A recordings, keyed as `${day_index}|${track}`.
  const [qaVideoDrafts, setQaVideoDrafts] = useState({});
  const [qaAudioDrafts, setQaAudioDrafts] = useState({});
  const [qaBusy, setQaBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, qaRes] = await Promise.all([
        api.get("/conference/schedule"),
        api.get("/conference/track-qa").catch(() => ({ data: { items: [] } })),
      ]);
      setSchedule(r.data.schedule || []);
      const linkInit = {};
      const videoInit = {};
      const audioInit = {};
      const pickInit = {};
      for (const d of (r.data.schedule || [])) {
        for (const s of (d.sessions || [])) {
          if ((s.links || []).length) linkInit[s.session_id] = s.links.map((l) => ({ ...l }));
          if (s.video_url) videoInit[s.session_id] = s.video_url;
          if (s.audio_url) audioInit[s.session_id] = s.audio_url;
          if (s.is_editors_pick) pickInit[s.session_id] = true;
        }
      }
      setDrafts(linkInit);
      setVideoDrafts(videoInit);
      setAudioDrafts(audioInit);
      setPickState(pickInit);
      const qaV = {};
      const qaA = {};
      for (const q of (qaRes.data?.items || [])) {
        const key = `${q.day_index}|${q.track}`;
        if (q.video_url) qaV[key] = q.video_url;
        if (q.audio_url) qaA[key] = q.audio_url;
      }
      setQaVideoDrafts(qaV);
      setQaAudioDrafts(qaA);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getLinks = (sid) => drafts[sid] || [];
  const setLinks = (sid, links) => setDrafts((p) => ({ ...p, [sid]: links }));
  const addLink = (sid) => setLinks(sid, [...getLinks(sid), { label: "", url: "" }]);
  const removeLink = (sid, idx) => setLinks(sid, getLinks(sid).filter((_, i) => i !== idx));
  const updateLink = (sid, idx, field, value) => {
    const next = getLinks(sid).map((l, i) => i === idx ? { ...l, [field]: value } : l);
    setLinks(sid, next);
  };

  const save = async (sid) => {
    setBusy((p) => ({ ...p, [sid]: true }));
    try {
      const links = (drafts[sid] || []).filter((l) => (l.url || "").trim());
      const video = (videoDrafts[sid] || "").trim();
      const audio = (audioDrafts[sid] || "").trim();
      await api.put(`/admin/sessions/${sid}/links`, { links });
      await api.put(`/admin/sessions/${sid}/video`, { video_url: video || null });
      await api.put(`/admin/sessions/${sid}/audio`, { audio_url: audio || null });
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy((p) => ({ ...p, [sid]: false })); }
  };

  const toggleEditorsPick = async (sid) => {
    const current = !!pickState[sid];
    setPickBusy((p) => ({ ...p, [sid]: true }));
    setPickState((p) => ({ ...p, [sid]: !current })); // optimistic
    try {
      await api.put(`/admin/sessions/${sid}/editors-pick`, { is_editors_pick: !current });
      toast.success(!current ? "Added to Editor's picks" : "Removed from Editor's picks");
    } catch (e) {
      setPickState((p) => ({ ...p, [sid]: current })); // revert
      toast.error(formatApiError(e));
    } finally {
      setPickBusy((p) => ({ ...p, [sid]: false }));
    }
  };

  const saveTrackQA = async (dayIndex, track) => {
    const key = `${dayIndex}|${track}`;
    setQaBusy((p) => ({ ...p, [key]: true }));
    try {
      const video_url = (qaVideoDrafts[key] || "").trim();
      const audio_url = (qaAudioDrafts[key] || "").trim();
      await api.put("/admin/track-qa", {
        day_index: dayIndex,
        track,
        video_url: video_url || null,
        audio_url: audio_url || null,
      });
      toast.success("Q+A saved for this block");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setQaBusy((p) => ({ ...p, [key]: false })); }
  };

  if (loading) return <div className="text-sm text-slate-500">Loading sessions…</div>;

  const day = schedule.find((d) => d.day_index === activeDay) || schedule[0];

  return (
    <div className="space-y-6" data-testid="admin-session-links">
      <div>
        <h2 className="font-heading text-2xl tracking-tight font-medium text-slate-900">Individual talks</h2>
        <p className="text-sm text-slate-500 mt-1">
          Attach the on-demand recording URL for each talk + any external resources (slides, papers, references). Viewers see a <span className="font-semibold">Watch talk</span> button when a video URL is set and clickable pills for each link.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {schedule.map((d) => (
          <Button
            key={d.day_index}
            variant={activeDay === d.day_index ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveDay(d.day_index)}
            className={"rounded-full text-xs " + (activeDay === d.day_index ? "bg-[#28BBE3] hover:bg-[#1FA8CC]" : "")}
            data-testid={`session-links-day-${d.day_index}`}
          >
            {d.day_label}
          </Button>
        ))}
      </div>

      <div className="space-y-6">
        {(() => {
          // Group active day's sessions by track (skip breaks/exhibition).
          const allSessions = day?.sessions || [];
          const BREAK_RX = /^(break|exhibition)/i;
          const groups = new Map();
          for (const s of allSessions) {
            const t = (s.track || "").trim();
            if (!t || BREAK_RX.test(t)) continue;
            if (!groups.has(t)) groups.set(t, []);
            groups.get(t).push(s);
          }
          // Sort groups by the earliest time of their first session.
          const blocks = [...groups.entries()]
            .map(([track, list]) => ({
              track,
              sessions: [...list].sort((a, b) =>
                (a.time || "").localeCompare(b.time || "")
                || ((a.sort_order ?? 99) - (b.sort_order ?? 99))
                || (a.title || "").localeCompare(b.title || "")
              ),
            }))
            .sort((a, b) =>
              (a.sessions[0]?.time || "").localeCompare(b.sessions[0]?.time || "")
              || a.track.localeCompare(b.track)
            );

          return blocks.map(({ track, sessions }) => {
            const blockKey = `${activeDay}|${track}`;
            const qaVideo = qaVideoDrafts[blockKey] || "";
            const qaAudio = qaAudioDrafts[blockKey] || "";
            const blockTime = sessions[0]?.time || "";
            return (
              <div key={blockKey} className="space-y-3" data-testid={`admin-block-${slugifyTrack(track)}`}>
                {/* Block-level Q+A editor */}
                <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 ring-1 ring-white/10 p-5 text-white shadow-[0_8px_30px_-12px_rgba(15,23,42,0.4)]">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-semibold flex items-center gap-2">
                        <span className="text-white/40">{blockTime}</span>
                        <span>·</span>
                        <span>{sessions.length} talk{sessions.length === 1 ? "" : "s"}</span>
                      </div>
                      <div className="mt-1 font-heading text-lg font-medium tracking-tight">{track}</div>
                      <div className="text-xs text-white/60 mt-0.5">Q+A recording for this block (covers all talks above).</div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => saveTrackQA(activeDay, track)}
                      disabled={qaBusy[blockKey]}
                      className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-xs shrink-0"
                      data-testid={`block-qa-save-${slugifyTrack(track)}`}
                    >
                      <Save size={12} className="mr-1" /> Save Q+A
                    </Button>
                  </div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-semibold">Q+A video URL (Vimeo) · optional</Label>
                      <Input
                        value={qaVideo}
                        onChange={(e) => setQaVideoDrafts((p) => ({ ...p, [blockKey]: e.target.value }))}
                        placeholder="https://vimeo.com/123456789/abc123"
                        className="mt-1 rounded-xl bg-white/5 border-white/10 text-white py-4 text-xs font-mono placeholder:text-white/30"
                        data-testid={`block-qa-video-${slugifyTrack(track)}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-semibold">Q+A audio URL (MP3) · optional</Label>
                      <Input
                        value={qaAudio}
                        onChange={(e) => setQaAudioDrafts((p) => ({ ...p, [blockKey]: e.target.value }))}
                        placeholder="https://cdn.example.com/qa.mp3"
                        className="mt-1 rounded-xl bg-white/5 border-white/10 text-white py-4 text-xs font-mono placeholder:text-white/30"
                        data-testid={`block-qa-audio-${slugifyTrack(track)}`}
                      />
                    </div>
                  </div>
                </div>

                {/* Talks in this block */}
                {sessions.map((s) => {
                  const links = drafts[s.session_id] || [];
                  const video = videoDrafts[s.session_id] || "";
                  const audio = audioDrafts[s.session_id] || "";
                  return (
            <div key={s.session_id} className="rounded-2xl bg-white ring-1 ring-black/5 p-5" data-testid={`session-links-${s.session_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                    <span className="text-slate-400">{s.time}</span>
                    <span>·</span>
                    <span className="text-slate-600">{s.track}</span>
                    {video && (
                      <span className="ml-2 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px]">VIDEO ATTACHED</span>
                    )}
                    {audio && (
                      <span className="ml-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[9px]">AUDIO ATTACHED</span>
                    )}
                  </div>
                  <div className="mt-1 font-medium text-slate-900">{s.title}</div>
                  {s.speaker && <div className="text-sm text-slate-500">{s.speaker}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleEditorsPick(s.session_id)}
                    disabled={pickBusy[s.session_id]}
                    className={
                      "rounded-full text-xs shrink-0 " +
                      (pickState[s.session_id]
                        ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200 hover:bg-amber-100 border-transparent"
                        : "")
                    }
                    data-testid={`editors-pick-toggle-${s.session_id}`}
                    title={pickState[s.session_id] ? "Remove from Editor's picks" : "Mark as Editor's pick"}
                  >
                    <Star
                      size={12}
                      className="mr-1"
                      fill={pickState[s.session_id] ? "currentColor" : "none"}
                    />
                    {pickState[s.session_id] ? "Picked" : "Pick"}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => save(s.session_id)}
                    disabled={busy[s.session_id]}
                    className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-xs shrink-0"
                    data-testid={`session-links-save-${s.session_id}`}
                  >
                    <Save size={12} className="mr-1" /> Save
                  </Button>
                </div>
              </div>

              <div className="mt-4">
                <Label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Talk video URL (Vimeo)</Label>
                <Input
                  value={video}
                  onChange={(e) => setVideoDrafts((p) => ({ ...p, [s.session_id]: e.target.value }))}
                  placeholder="https://vimeo.com/123456789/abc123 — leave empty to remove"
                  className="mt-1 rounded-xl border-slate-200 py-4 text-xs font-mono"
                  data-testid={`session-video-${s.session_id}`}
                />
              </div>

              <div className="mt-4">
                <Label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Podcast / audio-only URL (MP3)</Label>
                <Input
                  value={audio}
                  onChange={(e) => setAudioDrafts((p) => ({ ...p, [s.session_id]: e.target.value }))}
                  placeholder="https://cdn.example.com/talk.mp3 — leave empty to remove"
                  className="mt-1 rounded-xl border-slate-200 py-4 text-xs font-mono"
                  data-testid={`session-audio-${s.session_id}`}
                />
              </div>

              <div className="mt-4">
                <Label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">External links (slides, papers, references)</Label>
                <div className="mt-2 space-y-2">
                  {links.map((l, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        value={l.label}
                        onChange={(e) => updateLink(s.session_id, i, "label", e.target.value)}
                        placeholder="Label (e.g. Slides, Paper)"
                        className="rounded-xl border-slate-200 text-sm w-48"
                        data-testid={`session-link-label-${s.session_id}-${i}`}
                      />
                      <Input
                        value={l.url}
                        onChange={(e) => updateLink(s.session_id, i, "url", e.target.value)}
                        placeholder="https://..."
                        className="rounded-xl border-slate-200 text-xs font-mono flex-1"
                        data-testid={`session-link-url-${s.session_id}-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeLink(s.session_id, i)}
                        className="p-2 rounded-lg hover:bg-red-50 text-red-500 shrink-0"
                        data-testid={`session-link-remove-${s.session_id}-${i}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => addLink(s.session_id)}
                    className="rounded-full text-xs border-dashed"
                    data-testid={`session-link-add-${s.session_id}`}
                  >
                    <Plus size={12} className="mr-1" /> Add a link
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
              </div>
            );
          });
        })()}
      </div>
    </div>
  );
}


function Replays() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ day_index: 1, title: "", description: "", url: "" });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState({}); // {id: patch}
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/replays");
      setItems(r.data.replays || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim() || !form.url.trim()) {
      toast.error("Title and Vimeo URL are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/replays", {
        day_index: Number(form.day_index),
        title: form.title.trim(),
        description: form.description.trim() || null,
        url: form.url.trim(),
      });
      toast.success("Replay added (unpublished — toggle Publish when ready)");
      setForm({ day_index: form.day_index, title: "", description: "", url: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const setField = (id, field, value) => {
    setEditing((p) => ({ ...p, [id]: { ...(p[id] || {}), [field]: value } }));
  };

  const save = async (item) => {
    const patch = editing[item.replay_id] || {};
    if (Object.keys(patch).length === 0) return;
    setBusy((p) => ({ ...p, [item.replay_id]: true }));
    try {
      const payload = {
        day_index: patch.day_index != null ? Number(patch.day_index) : item.day_index,
        title: (patch.title ?? item.title)?.trim() || item.title,
        description: patch.description != null ? patch.description : (item.description || ""),
        url: (patch.url ?? item.url)?.trim() || item.url,
        sort_order: patch.sort_order != null ? Number(patch.sort_order) : item.sort_order,
      };
      await api.put(`/admin/replays/${item.replay_id}`, payload);
      toast.success("Saved");
      setEditing((p) => { const c = { ...p }; delete c[item.replay_id]; return c; });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy((p) => ({ ...p, [item.replay_id]: false })); }
  };

  const togglePublish = async (item) => {
    setBusy((p) => ({ ...p, [item.replay_id]: true }));
    try {
      await api.put(`/admin/replays/${item.replay_id}`, {
        day_index: item.day_index,
        title: item.title,
        url: item.url,
        published: !item.published,
      });
      toast.success(!item.published ? "Published — viewers can see it" : "Hidden from viewers");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy((p) => ({ ...p, [item.replay_id]: false })); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This can't be undone.`)) return;
    setBusy((p) => ({ ...p, [item.replay_id]: true }));
    try {
      await api.delete(`/admin/replays/${item.replay_id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy((p) => ({ ...p, [item.replay_id]: false })); }
  };

  const byDay = new Map();
  for (const it of items) {
    if (!byDay.has(it.day_index)) byDay.set(it.day_index, []);
    byDay.get(it.day_index).push(it);
  }
  const days = [...byDay.keys()].sort();
  const DAY_LABEL = { 1: "Day 1 — Mon 22 Jun", 2: "Day 2 — Tue 23 Jun", 3: "Day 3 — Wed 24 Jun" };

  return (
    <div className="space-y-6" data-testid="admin-replays">
      <div>
        <h2 className="font-heading text-2xl tracking-tight font-medium text-slate-900">Watch-back replays</h2>
        <p className="text-sm text-slate-500 mt-1">
          Add Vimeo recordings for each completed live session. Toggle <span className="font-semibold">Publish</span> to show them in the viewer Portal.
        </p>
      </div>

      {/* Add new */}
      <div className="rounded-3xl bg-white ring-1 ring-black/5 p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 rounded-full bg-[#28BBE3]/10 flex items-center justify-center">
            <Plus className="text-[#28BBE3]" size={18} strokeWidth={1.8} />
          </div>
          <div>
            <div className="font-heading text-lg font-bold text-slate-900">Add a replay</div>
            <div className="text-xs text-slate-500">Stays unpublished until you flip the toggle.</div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Day</Label>
            <select
              value={form.day_index}
              onChange={(e) => setForm((p) => ({ ...p, day_index: Number(e.target.value) }))}
              className="mt-2 w-full rounded-xl border border-slate-200 bg-white py-3 px-3 text-sm"
              data-testid="replay-new-day"
            >
              <option value={1}>Day 1</option>
              <option value={2}>Day 2</option>
              <option value={3}>Day 3</option>
            </select>
          </div>
          <div className="md:col-span-4">
            <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              placeholder="e.g. Day 1 — Keynote 1: Basics"
              className="mt-2 rounded-xl border-slate-200 py-5"
              data-testid="replay-new-title"
            />
          </div>
          <div className="md:col-span-6">
            <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Vimeo URL</Label>
            <Input
              value={form.url}
              onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))}
              placeholder="https://vimeo.com/event/… or https://vimeo.com/123456789/abc"
              className="mt-2 rounded-xl border-slate-200 py-5 font-mono text-xs"
              data-testid="replay-new-url"
            />
          </div>
          <div className="md:col-span-12">
            <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Description (optional)</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="e.g. Becky Platt · Ken Milne · Suzanne Crowe"
              className="mt-2 rounded-xl border-slate-200"
              rows={2}
              data-testid="replay-new-desc"
            />
          </div>
        </div>
        <div className="mt-4">
          <Button
            onClick={create}
            disabled={saving}
            className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white px-6"
            data-testid="replay-new-submit"
          >
            <Plus size={14} className="mr-1.5" />
            {saving ? "Adding…" : "Add replay"}
          </Button>
        </div>
      </div>

      {/* Existing list */}
      {loading ? (
        <div className="text-sm text-slate-500">Loading replays…</div>
      ) : days.length === 0 ? (
        <div className="rounded-3xl bg-white ring-1 ring-black/5 p-10 text-center text-slate-500 text-sm">
          No replays yet. Use the form above to add the first one.
        </div>
      ) : (
        <div className="space-y-6">
          {days.map((di) => (
            <div key={di}>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-3">{DAY_LABEL[di] || `Day ${di}`}</div>
              <div className="space-y-3">
                {byDay.get(di).map((it) => {
                  const draft = editing[it.replay_id] || {};
                  const dirty = Object.keys(draft).length > 0;
                  return (
                    <div key={it.replay_id} className="rounded-2xl bg-white ring-1 ring-black/5 p-5" data-testid={`replay-card-${it.replay_id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-12 gap-3">
                          <div className="md:col-span-5">
                            <Label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Title</Label>
                            <Input
                              value={draft.title ?? it.title}
                              onChange={(e) => setField(it.replay_id, "title", e.target.value)}
                              className="mt-1 rounded-xl border-slate-200 py-4 text-sm"
                            />
                          </div>
                          <div className="md:col-span-5">
                            <Label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Vimeo URL</Label>
                            <Input
                              value={draft.url ?? it.url}
                              onChange={(e) => setField(it.replay_id, "url", e.target.value)}
                              className="mt-1 rounded-xl border-slate-200 py-4 text-xs font-mono"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Order</Label>
                            <Input
                              type="number"
                              value={draft.sort_order ?? it.sort_order ?? 0}
                              onChange={(e) => setField(it.replay_id, "sort_order", e.target.value)}
                              className="mt-1 rounded-xl border-slate-200 py-4 text-sm"
                            />
                          </div>
                          <div className="md:col-span-12">
                            <Label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Description</Label>
                            <Input
                              value={draft.description ?? it.description ?? ""}
                              onChange={(e) => setField(it.replay_id, "description", e.target.value)}
                              className="mt-1 rounded-xl border-slate-200 py-4 text-sm"
                            />
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2 shrink-0">
                          <Badge className={
                            "rounded-full px-3 py-1 border-0 " +
                            (it.published ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-100 text-slate-600 hover:bg-slate-100")
                          }>
                            {it.published ? <Eye size={12} className="mr-1" /> : <EyeOff size={12} className="mr-1" />}
                            {it.published ? "Published" : "Hidden"}
                          </Badge>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => togglePublish(it)}
                              disabled={busy[it.replay_id]}
                              className="rounded-full text-xs"
                              data-testid={`replay-toggle-${it.replay_id}`}
                            >
                              {it.published ? "Hide" : "Publish"}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => save(it)}
                              disabled={!dirty || busy[it.replay_id]}
                              className="rounded-full text-xs bg-[#28BBE3] hover:bg-[#1FA8CC]"
                              data-testid={`replay-save-${it.replay_id}`}
                            >
                              <Save size={12} className="mr-1" /> Save
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => remove(it)}
                              disabled={busy[it.replay_id]}
                              className="rounded-full text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`replay-delete-${it.replay_id}`}
                            >
                              <Trash2 size={12} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Support() {
  const [status, setStatus] = useState("open");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/support/tickets", { params: { status } });
      setTickets(r.data.tickets || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const act = async (ticket_id, action) => {
    setBusy((p) => ({ ...p, [ticket_id]: true }));
    try {
      await api.post(`/admin/support/tickets/${ticket_id}/${action}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((p) => ({ ...p, [ticket_id]: false }));
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-support-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Support</div>
          <div className="mt-1 font-heading text-2xl tracking-tight text-slate-900">
            {tickets.length} {status === "all" ? "tickets" : `${status} tickets`}
          </div>
        </div>
        <div className="flex rounded-full bg-white ring-1 ring-black/5 p-1 text-sm">
          {["open", "resolved", "all"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={`px-4 py-1.5 rounded-full transition capitalize ${
                status === s ? "bg-[#28BBE3] text-white" : "text-slate-600"
              }`}
              data-testid={`support-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {tickets.length === 0 ? (
        <div className="rounded-3xl bg-white ring-1 ring-black/5 p-12 text-center text-slate-400">
          {loading ? "Loading…" : `No ${status === "all" ? "" : status} tickets.`}
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => (
            <div
              key={t.ticket_id}
              className="rounded-2xl bg-white ring-1 ring-black/5 p-5"
              data-testid={`support-ticket-${t.ticket_id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className={`rounded-full px-2.5 py-0.5 font-semibold uppercase tracking-wider ${
                      t.status === "open"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-emerald-100 text-emerald-700"
                    }`}>{t.status}</span>
                    {t.category && (
                      <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-slate-600">{t.category}</span>
                    )}
                    <span className="text-slate-400">{new Date(t.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}</span>
                  </div>
                  <p className="mt-3 text-sm text-slate-900 leading-relaxed whitespace-pre-wrap">{t.message}</p>
                  <div className="mt-3 text-xs text-slate-500">
                    <a href={`mailto:${t.user_email}`} className="text-[#28BBE3] hover:underline font-medium">
                      {t.user_name || t.user_email}
                    </a>
                    {t.user_name && <span className="text-slate-400"> · {t.user_email}</span>}
                  </div>
                  {(t.page_url || t.user_agent) && (
                    <details className="mt-2 text-xs text-slate-400">
                      <summary className="cursor-pointer hover:text-slate-600">Debug info</summary>
                      <div className="mt-1.5 space-y-1">
                        {t.page_url && <div className="break-all">Page: <span className="text-slate-600">{t.page_url}</span></div>}
                        {t.user_agent && <div className="break-all">UA: <span className="text-slate-600 font-mono">{t.user_agent}</span></div>}
                      </div>
                    </details>
                  )}
                </div>
                <div className="flex flex-col gap-2 items-stretch">
                  {t.status === "open" ? (
                    <Button
                      size="sm"
                      className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white"
                      disabled={!!busy[t.ticket_id]}
                      onClick={() => act(t.ticket_id, "resolve")}
                      data-testid={`support-resolve-${t.ticket_id}`}
                    >
                      Mark resolved
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full border-slate-300 text-slate-600"
                      disabled={!!busy[t.ticket_id]}
                      onClick={() => act(t.ticket_id, "reopen")}
                      data-testid={`support-reopen-${t.ticket_id}`}
                    >
                      Reopen
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QnA() {
  const [link, setLink] = useState(null);
  const [copyState, setCopyState] = useState("idle");
  const [rotating, setRotating] = useState(false);

  const loadLink = useCallback(async () => {
    try {
      const r = await api.get("/admin/chair-link");
      setLink(r.data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { loadLink(); }, [loadLink]);

  const copy = async () => {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 1500);
    } catch { /* ignore */ }
  };

  const rotate = async () => {
    if (!window.confirm("Generate a new chair link?\n\nThe existing link will stop working immediately. Anyone currently using it (e.g. a chair on stage) will need the new URL.")) return;
    setRotating(true);
    try {
      const r = await api.post("/admin/chair-link/rotate");
      setLink(r.data);
      toast.success("Chair link rotated. Old URL no longer works.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRotating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-qna-tab">
      <div className="rounded-3xl bg-white ring-1 ring-black/5 p-6 sm:p-7">
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Chair console link</div>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-2xl">
          Share this URL with session chairs so they can see live questions on their own device without signing in as an admin. Anyone with the link can mark questions as asked or dismiss them — rotate it if it ever leaks.
        </p>
        {link?.url ? (
          <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="flex-1 rounded-2xl bg-slate-50 ring-1 ring-slate-200 px-4 py-3 text-xs font-mono text-slate-700 truncate" data-testid="chair-link-url">
              {link.url}
            </div>
            <Button
              variant="outline"
              className="rounded-full border-slate-300"
              onClick={copy}
              data-testid="chair-link-copy-btn"
            >
              {copyState === "copied" ? "Copied!" : "Copy"}
            </Button>
            <Button
              variant="ghost"
              className="rounded-full text-slate-600"
              onClick={rotate}
              disabled={rotating}
              data-testid="chair-link-rotate-btn"
            >
              {rotating ? "Rotating…" : "Rotate"}
            </Button>
          </div>
        ) : (
          <div className="mt-4 text-xs text-slate-400">Loading link…</div>
        )}
      </div>

      <QAConsole title="Live questions" subtitle="Submitted by virtual viewers · auto-refreshes" />
    </div>
  );
}

function Admins() {
  const { user: currentAdmin } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/attendees", { params: { filter: "admin", limit: 100 } });
      setAdmins(r.data.users || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const inviteAdmin = async () => {
    const email = window.prompt(
      "Invite a new admin by email.\n\n" +
      "If they already have a DFTB26 account, they'll just be promoted to admin. " +
      "If not, a new account will be created and they'll receive a welcome email with login details.",
    );
    if (!email) return;
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("That doesn't look like a valid email address.");
      return;
    }
    const name = window.prompt("Optional: full name for the welcome email (leave blank to use the email prefix)", "") || "";
    try {
      await api.post("/admin/admins/invite", { email: trimmed, name: name.trim(), send_email: true });
      toast.success(`${trimmed} invited as admin and emailed their login.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const revokeAdmin = async (user_id, email) => {
    if (!window.confirm(
      `Revoke admin rights from ${email}?\n\n` +
      `They will lose access to the admin dashboard but their ticket and account remain intact.`,
    )) return;
    try {
      await api.post(`/admin/users/${user_id}/revoke-admin`);
      toast.success(`Admin rights revoked from ${email}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-admins-tab">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Admin team</div>
          <div className="mt-1 font-heading text-2xl tracking-tight text-slate-900">
            {admins.length} {admins.length === 1 ? "admin" : "admins"}
          </div>
        </div>
        <Button onClick={inviteAdmin} className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white" data-testid="admins-invite-btn">
          <Plus size={15} className="mr-2" /> Invite admin
        </Button>
      </div>

      <div className="rounded-3xl bg-white ring-1 ring-black/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
              <tr>
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Email</th>
                <th className="px-5 py-3">Auth</th>
                <th className="px-5 py-3">Added</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {admins.map((u) => (
                <tr key={u.user_id} data-testid={`admin-row-${u.user_id}`} className="hover:bg-slate-50/50">
                  <td className="px-5 py-3 text-slate-900 font-medium whitespace-nowrap">
                    {u.name || <span className="text-slate-400 italic">—</span>}
                  </td>
                  <td className="px-5 py-3 text-slate-700 truncate max-w-[280px]">{u.email}</td>
                  <td className="px-5 py-3 text-slate-500 text-xs capitalize">{u.auth_provider || "password"}</td>
                  <td className="px-5 py-3 text-slate-500 font-mono text-xs">{(u.created_at || "").slice(0, 10)}</td>
                  <td className="px-5 py-3 text-right">
                    {u.user_id === currentAdmin?.user_id ? (
                      <span className="text-xs text-slate-400 italic">You</span>
                    ) : (
                      <button onClick={() => revokeAdmin(u.user_id, u.email)}
                        className="text-xs text-rose-600 hover:underline inline-flex items-center gap-1"
                        data-testid={`admins-revoke-${u.user_id}`}>
                        <ShieldOff size={11} /> Revoke admin
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && admins.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-12 text-center text-slate-400">No admins yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]"><div className="w-10 h-10 rounded-full border-2 border-[#28BBE3] border-t-transparent animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/portal" replace />;

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="admin-page">
      <div className="border-b border-black/5 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
          <Link to="/portal" className="flex items-center gap-3 text-slate-700 hover:text-slate-900" data-testid="admin-back-link">
            <ArrowLeft size={16} /> <span className="text-sm">Back to portal</span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold hidden sm:block">Admin · DFTB26</div>
            <NotificationBell />
            <Button variant="ghost" size="sm" onClick={async () => { await logout(); navigate("/"); }} data-testid="admin-logout-btn">
              <LogOut size={14} className="mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Admin dashboard</div>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tighter font-medium text-slate-900">
            Run the conference, <span className="italic font-normal text-[#28BBE3]">end to end.</span>
          </h1>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <div className="-mx-2 px-2 overflow-x-auto admin-tabs-scroll">
            <TabsList className="bg-white ring-1 ring-black/5 p-1 rounded-full h-auto flex w-max">
              <TabsTrigger value="overview" className="rounded-full px-5 py-2 data-[state=active]:bg-[#28BBE3] data-[state=active]:text-white" data-testid="admin-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="attendees" className="rounded-full px-5 py-2 data-[state=active]:bg-[#28BBE3] data-[state=active]:text-white" data-testid="admin-tab-attendees">Attendees</TabsTrigger>
              <TabsTrigger value="admins" className="rounded-full px-5 py-2 data-[state=active]:bg-[#28BBE3] data-[state=active]:text-white" data-testid="admin-tab-admins">Admins</TabsTrigger>
              <TabsTrigger value="qna" className="rounded-full px-5 py-2 data-[state=active]:bg-[#28BBE3] data-[state=active]:text-white" data-testid="admin-tab-qna">Q&amp;A</TabsTrigger>
              <TabsTrigger value="support" className="rounded-full px-5 py-2 data-[state=active]:bg-[#28BBE3] data-[state=active]:text-white" data-testid="admin-tab-support">Support</TabsTrigger>
              <TabsTrigger value="rooms" className="rounded-full px-5 py-2 data-[state=active]:bg-[#28BBE3] data-[state=active]:text-white" data-testid="admin-tab-rooms">Rooms</TabsTrigger>
              <TabsTrigger value="replays" className="rounded-full px-5 py-2 data-[state=active]:bg-[#28BBE3] data-[state=active]:text-white" data-testid="admin-tab-replays">Replays</TabsTrigger>
              <TabsTrigger value="sessions" className="rounded-full px-5 py-2 data-[state=active]:bg-[#28BBE3] data-[state=active]:text-white" data-testid="admin-tab-sessions">Sessions</TabsTrigger>
              <TabsTrigger value="session-links" className="rounded-full px-5 py-2 data-[state=active]:bg-[#28BBE3] data-[state=active]:text-white" data-testid="admin-tab-session-links">Talks</TabsTrigger>
              <TabsTrigger value="lmic" className="rounded-full px-5 py-2 data-[state=active]:bg-[#28BBE3] data-[state=active]:text-white" data-testid="admin-tab-lmic">LMIC</TabsTrigger>
              <TabsTrigger value="transactions" className="rounded-full px-5 py-2 data-[state=active]:bg-[#28BBE3] data-[state=active]:text-white" data-testid="admin-tab-transactions">Transactions</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="overview" className="mt-8"><Overview /></TabsContent>
          <TabsContent value="attendees" className="mt-8"><Attendees /></TabsContent>
          <TabsContent value="admins" className="mt-8"><Admins /></TabsContent>
          <TabsContent value="qna" className="mt-8"><QnA /></TabsContent>
          <TabsContent value="support" className="mt-8"><Support /></TabsContent>
          <TabsContent value="rooms" className="mt-8"><Rooms /></TabsContent>
          <TabsContent value="replays" className="mt-8"><Replays /></TabsContent>
          <TabsContent value="sessions" className="mt-8"><Sessions /></TabsContent>
          <TabsContent value="session-links" className="mt-8"><SessionLinks /></TabsContent>
          <TabsContent value="lmic" className="mt-8"><LMIC /></TabsContent>
          <TabsContent value="transactions" className="mt-8"><Transactions /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

